/**
 * Format file size in human-readable format
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Format date in localized format
 */
export const formatDate = (dateString, options = {}) => {
  const date = new Date(dateString);
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };

  return date.toLocaleDateString('hu-HU', { ...defaultOptions, ...options });
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;

  return formatDate(dateString, { hour: undefined, minute: undefined });
};

/**
 * Supported 3D model extensions
 */
export const MODEL_EXTENSIONS = ['stl', 'obj', '3mf'];

/**
 * Check if extension is a 3D model
 */
export const is3DModel = (extension) => MODEL_EXTENSIONS.includes(extension?.toLowerCase());

/**
 * Get file icon based on extension/mime type
 */
export const getFileIcon = (extension, mimeType) => {
  const ext = extension?.toLowerCase();

  if (ext === 'pdf' || mimeType?.includes('pdf')) return 'file-text';
  if (ext === 'docx' || ext === 'doc' || mimeType?.includes('word')) return 'file-text';
  if (ext === 'xlsx' || ext === 'xls' || mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return 'table';
  if (mimeType?.startsWith('image/')) return 'image';
  if (mimeType?.startsWith('video/') || ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(ext)) return 'video';
  if (is3DModel(ext)) return 'model';

  return 'file';
};

/**
 * Get file type label
 */
export const getFileTypeLabel = (extension) => {
  const ext = extension?.toLowerCase();

  switch (ext) {
    case 'pdf': return 'PDF document';
    case 'docx':
    case 'doc': return 'Word document';
    case 'xlsx':
    case 'xls': return 'Excel spreadsheet';
    case 'jpg':
    case 'jpeg': return 'JPEG image';
    case 'png': return 'PNG image';
    case 'gif': return 'GIF image';
    case 'webp': return 'WebP image';
    case 'svg': return 'SVG image';
    case 'mp4': return 'MP4 video';
    case 'webm': return 'WebM video';
    case 'ogg': return 'OGG video';
    case 'mov': return 'QuickTime video';
    case 'avi': return 'AVI video';
    case 'mkv': return 'MKV video';
    case 'stl': return 'STL 3D model';
    case 'obj': return 'OBJ 3D model';
    case '3mf': return '3MF 3D model';
    default: return ext?.toUpperCase() || 'File';
  }
};

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Generate initials from name
 */
export const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Generate color from string (for avatars)
 */
export const stringToColor = (str) => {
  if (!str) return '#6366f1';

  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const colors = [
    '#ef4444', '#f97316', '#f59e0b', '#eab308',
    '#84cc16', '#22c55e', '#10b981', '#14b8a6',
    '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
    '#8b5cf6', '#a855f7', '#d946ef', '#ec4899',
    '#f43f5e'
  ];

  return colors[Math.abs(hash) % colors.length];
};

/**
 * Check if file is viewable
 */
export const isViewable = (mimeType) => {
  const viewableMimes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword',
    'application/vnd.ms-excel',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'video/mp4',
    'video/webm',
    'video/ogg',
    'video/quicktime',
    'video/x-msvideo',
    'video/x-matroska',
    'model/stl',
    'model/obj',
    'model/3mf'
  ];

  return viewableMimes.includes(mimeType);
};

/**
 * Maximum file upload size in bytes (800MB)
 */
export const MAX_FILE_SIZE = 800 * 1024 * 1024;

/**
 * Download blob as file
 */
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};
